import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Mail, Wrench, TriangleAlert, CheckCircle2, Save, BellRing, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function Maintenance() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [plannedStart, setPlannedStart] = useState("08:30");
  const [saving, setSaving] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  useEffect(() => {
    if (settings?.maintenance_email) setEmail(settings.maintenance_email);
    if (settings?.timeline_planned_start) setPlannedStart(settings.timeline_planned_start);
  }, [settings]);

  const { data: issues = [], isLoading } = useQuery({
    queryKey: ["issues"],
    queryFn: async () => (await api.get("/issues")).data,
    refetchInterval: 20000,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => (await api.get("/managers")).data,
  });

  const saveSettings = async (partial, successMsg) => {
    setSaving(true);
    try {
      await api.put("/settings", partial);
      if (successMsg) toast.success(successMsg);
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const saveEmail = () => saveSettings({ maintenance_email: email.trim() }, t("Maintenance email updated"));

  const resolve = useMutation({
    mutationFn: ({ cleanId, issueId }) => api.post(`/issues/${cleanId}/${issueId}/resolve`),
    onSuccess: () => {
      toast.success(t("Marked resolved"));
      qc.invalidateQueries({ queryKey: ["issues"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const open = issues.filter((i) => i.status === "open");
  const resolved = issues.filter((i) => i.status !== "open");
  const maintOn = !!settings?.maintenance_email_enabled;
  const mgrRemOn = !!settings?.manager_reminders_email_enabled;
  const recipientIds = settings?.reminder_recipient_ids || [];

  const toggleRecipient = (id) => {
    const next = recipientIds.includes(id)
      ? recipientIds.filter((r) => r !== id)
      : [...recipientIds, id];
    saveSettings({ reminder_recipient_ids: next });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold">{t("Maintenance")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("Problems reported by cleaners. Automatic emails are off by default — turn on the ones you want below.")}
        </p>
      </div>

      {/* automatic email settings */}
      <Card className="rounded-md border p-5" data-testid="auto-email-settings">
        <div className="label-caps mb-4 flex items-center gap-2 text-muted-foreground">
          <BellRing className="h-4 w-4" /> {t("Automatic emails")}
        </div>

        <div className="flex items-start justify-between gap-3 border-b border-dashed border-border pb-4">
          <div>
            <div className="text-sm font-semibold">{t("Maintenance reports")}</div>
            <p className="text-xs text-muted-foreground">{t("Email your office a report of a room's reported problems when its clean is completed.")}</p>
          </div>
          <Switch
            checked={maintOn}
            onCheckedChange={(v) => saveSettings({ maintenance_email_enabled: v }, v ? t("Maintenance emails on") : t("Maintenance emails off"))}
            data-testid="maintenance-email-toggle"
          />
        </div>

        <div className="flex items-start justify-between gap-3 py-4">
          <div>
            <div className="text-sm font-semibold">{t("Manager to-do reminders")}</div>
            <p className="text-xs text-muted-foreground">{t("Email a daily reminder when scheduled manager tasks are due.")}</p>
          </div>
          <Switch
            checked={mgrRemOn}
            onCheckedChange={(v) => saveSettings({ manager_reminders_email_enabled: v }, v ? t("Reminder emails on") : t("Reminder emails off"))}
            data-testid="manager-reminders-toggle"
          />
        </div>

        {mgrRemOn && (
          <div className="mt-1 space-y-2 border-t border-dashed border-border pt-4" data-testid="reminder-recipients">
            <div className="text-sm font-semibold">{t("Who gets the reminder?")}</div>
            <p className="text-xs text-muted-foreground">
              {t("Pick the managers who should receive the daily to-do reminder. No email is sent until at least one is selected.")}
            </p>
            <div className="mt-2 space-y-1.5">
              {managers.length === 0 && (
                <p className="text-xs text-muted-foreground">{t("No managers found.")}</p>
              )}
              {managers.map((m) => (
                <label
                  key={m.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/40"
                  data-testid={`recipient-row-${m.id}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-foreground"
                    checked={recipientIds.includes(m.id)}
                    onChange={() => toggleRecipient(m.id)}
                    disabled={saving}
                    data-testid={`recipient-checkbox-${m.id}`}
                  />
                  <span className="font-medium">{m.name}</span>
                  <span className="text-xs text-muted-foreground">{m.email}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-1 flex flex-col gap-3 border-t border-dashed border-border pt-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {t("Office / maintenance email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="maintenance@yourmotel.co.nz"
              data-testid="maintenance-email-input"
            />
          </div>
          <Button onClick={saveEmail} disabled={saving || !email} data-testid="save-maintenance-email-btn" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("Save")}
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{t("Maintenance reports default to your account email if no office email is set.")}</p>
      </Card>

      {/* planned timeline start */}
      <Card className="rounded-md border p-5" data-testid="planned-start-settings">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="font-display text-lg font-extrabold">{t("Planned start time")}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("The time the day is planned to begin. The Timeline uses it to project the planned finish (grey line) before anyone clocks in. A rostered start for the weekday, if set, takes priority.")}
        </p>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-40 space-y-1.5">
            <Label>{t("Start time")}</Label>
            <Input
              type="time"
              value={plannedStart}
              onChange={(e) => setPlannedStart(e.target.value)}
              data-testid="planned-start-input"
            />
          </div>
          <Button
            onClick={() => saveSettings({ timeline_planned_start: plannedStart }, t("Planned start saved"))}
            disabled={saving || !plannedStart}
            data-testid="save-planned-start-btn"
            className="gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {t("Save")}
          </Button>
        </div>
      </Card>

      {/* open issues */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : issues.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 rounded-md border border-dashed py-20 text-center">
          <Wrench className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("No problems reported. Everything's shipshape.")}</p>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="issues-list">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <TriangleAlert className="h-4 w-4 text-amber-600" />
              <span className="font-display font-bold">{t("Open ({{count}})", { count: open.length })}</span>
            </div>
            <div className="space-y-2">
              {open.length === 0 && <p className="text-sm text-muted-foreground">{t("No open issues.")}</p>}
              {open.map((i) => (
                <IssueRow key={i.id} issue={i} onResolve={resolve.mutate} />
              ))}
            </div>
          </div>

          {resolved.length > 0 && (
            <div>
              <div className="mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="font-display font-bold">{t("Resolved ({{count}})", { count: resolved.length })}</span>
              </div>
              <div className="space-y-2 opacity-70">
                {resolved.map((i) => (
                  <IssueRow key={i.id} issue={i} onResolve={resolve.mutate} resolved />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IssueRow({ issue, onResolve, resolved }) {
  const { t } = useTranslation();
  return (
    <Card
      data-testid={`issue-row-${issue.id}`}
      className="flex flex-col gap-2 rounded-md border p-4 sm:flex-row sm:items-center sm:gap-4"
    >
      <div className="min-w-[90px] font-display text-lg font-extrabold">{issue.room_label}</div>
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">{issue.category}</Badge>
          <span className="text-xs text-muted-foreground">
            {issue.cleaner_name} · {issue.date}
          </span>
        </div>
        {issue.note && <p className="mt-1 text-sm text-foreground">{issue.note}</p>}
      </div>
      {!resolved ? (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onResolve({ cleanId: issue.clean_id, issueId: issue.id })}
          data-testid={`resolve-issue-${issue.id}`}
          className="gap-1.5"
        >
          <CheckCircle2 className="h-4 w-4" /> {t("Resolve")}
        </Button>
      ) : (
        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("Resolved")}</Badge>
      )}
    </Card>
  );
}
